import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiArrowLeft, FiArrowRight, FiPackage, FiDatabase } from 'react-icons/fi';
import { networkManager, BankItemData, ShipInventoryItem, InventoryData } from '../../systems/NetworkManager';
import { emitGameEvent } from '../../core/GameEvents';
import { getEquipmentById, RESOURCES, Equipment } from '@astra/shared';
import {
  PALETTE, SLOT_COLORS, RESOURCE_COLORS,
  stationContainerStyle, stationHeaderStyle, stationBackButtonStyle,
  stationHeaderTabsStyle, stationHeaderTabStyle, stationHintStyle, stationFooterStyle,
} from './stationTheme';

// Filter options
type ItemFilter = 'all' | 'equipment' | 'resource';

interface FilterOption {
  id: ItemFilter;
  label: string;
}

const FILTERS: FilterOption[] = [
  { id: 'all', label: 'All' },
  { id: 'equipment', label: 'Equipment' },
  { id: 'resource', label: 'Resources' },
];

interface BankProps {
  stationId: string;
  onBack: () => void;
}

interface DisplayItem {
  itemType: 'equipment' | 'resource';
  itemId: string;
  quantity: number;
  name: string;
  color: string;
  tier?: number;
  slotType?: string;
}

function getItemDisplayInfo(itemType: 'equipment' | 'resource', itemId: string, quantity: number): DisplayItem | null {
  if (itemType === 'equipment') {
    const equipment = getEquipmentById(itemId);
    if (!equipment) return null;
    return {
      itemType,
      itemId,
      quantity,
      name: equipment.name,
      color: SLOT_COLORS[equipment.slotType] || PALETTE.DIM,
      tier: equipment.tier,
      slotType: equipment.slotType,
    };
  } else {
    const resourceId = itemId.replace('resource_', '');
    const resourceInfo = RESOURCES[resourceId.toUpperCase() as keyof typeof RESOURCES];
    return {
      itemType,
      itemId,
      quantity,
      name: resourceInfo?.name || resourceId,
      color: RESOURCE_COLORS[resourceId] || PALETTE.TEAL,
    };
  }
}

export function Bank({ stationId, onBack }: BankProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState<'bank' | 'ship'>('bank');

  // Bank items
  const [bankItems, setBankItems] = useState<BankItemData[]>([]);
  const [bankLoading, setBankLoading] = useState(true);

  // Ship inventory
  const [shipInventory, setShipInventory] = useState<ShipInventoryItem[]>([]);
  const [shipLoading, setShipLoading] = useState(true);

  // Selection and filter state
  const [selectedBankItemId, setSelectedBankItemId] = useState<string>('');
  const [selectedShipItemId, setSelectedShipItemId] = useState<string>('');
  const [bankFilter, setBankFilter] = useState<ItemFilter>('all');
  const [shipFilter, setShipFilter] = useState<ItemFilter>('all');

  // Transfer state
  const [transferring, setTransferring] = useState(false);
  const [transferQuantity, setTransferQuantity] = useState(1);

  // Load bank items
  const loadBank = useCallback(() => {
    setBankLoading(true);
    networkManager.requestPlayerBank();
  }, []);

  // Load ship inventory
  const loadShipInventory = useCallback(() => {
    setShipLoading(true);
    networkManager.requestInventoryData('');
  }, []);

  // Initial load
  useEffect(() => {
    loadBank();
    loadShipInventory();
  }, [loadBank, loadShipInventory]);

  // Handle bank data
  useEffect(() => {
    const prevHandler = networkManager.onPlayerBank;

    networkManager.onPlayerBank = (data) => {
      if (data.error) {
        emitGameEvent('notification', { type: 'error', message: data.error });
        setBankItems([]);
      } else {
        setBankItems(data.items);
      }
      setBankLoading(false);
    };

    return () => {
      networkManager.onPlayerBank = prevHandler;
    };
  }, []);

  // Handle inventory data
  useEffect(() => {
    const prevHandler = networkManager.onInventoryData;

    networkManager.onInventoryData = (data: InventoryData) => {
      setShipInventory(data.shipInventory || []);
      setShipLoading(false);
    };

    return () => {
      networkManager.onInventoryData = prevHandler;
    };
  }, []);

  // Handle transfer result
  useEffect(() => {
    const prevHandler = networkManager.onBankTransferResult;

    networkManager.onBankTransferResult = (data) => {
      setTransferring(false);
      if (data.success) {
        emitGameEvent('notification', { type: 'success', message: 'Transfer complete!' });
        // Refresh both bank and ship inventory
        loadBank();
        loadShipInventory();
        // Reset selection
        setSelectedBankItemId('');
        setSelectedShipItemId('');
        setTransferQuantity(1);
      } else {
        emitGameEvent('notification', { type: 'error', message: data.error || 'Transfer failed' });
      }
    };

    return () => {
      networkManager.onBankTransferResult = prevHandler;
    };
  }, [loadBank, loadShipInventory]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onBack]);

  // Filter and transform bank items
  const displayBankItems = useMemo(() => {
    return bankItems
      .map(item => getItemDisplayInfo(item.itemType, item.itemId, item.quantity))
      .filter((item): item is DisplayItem => item !== null)
      .filter(item => bankFilter === 'all' || item.itemType === bankFilter);
  }, [bankItems, bankFilter]);

  // Filter and transform ship items
  const displayShipItems = useMemo(() => {
    return shipInventory
      .map(item => getItemDisplayInfo(item.itemType, item.itemId, item.quantity))
      .filter((item): item is DisplayItem => item !== null)
      .filter(item => shipFilter === 'all' || item.itemType === shipFilter);
  }, [shipInventory, shipFilter]);

  // Get selected items
  const selectedBankItem = displayBankItems.find(i => i.itemId === selectedBankItemId);
  const selectedShipItem = displayShipItems.find(i => i.itemId === selectedShipItemId);

  // Get equipment details for selected item
  const getSelectedEquipmentDetails = (item: DisplayItem | undefined): Equipment | null => {
    if (!item || item.itemType !== 'equipment') return null;
    return getEquipmentById(item.itemId) || null;
  };

  const selectedBankEquipment = getSelectedEquipmentDetails(selectedBankItem);
  const selectedShipEquipment = getSelectedEquipmentDetails(selectedShipItem);

  // Transfer handlers
  const handleBankToShip = useCallback(() => {
    if (!selectedBankItem || transferring) return;
    setTransferring(true);
    networkManager.sendBankToShip(selectedBankItem.itemType, selectedBankItem.itemId, transferQuantity);
  }, [selectedBankItem, transferQuantity, transferring]);

  const handleShipToBank = useCallback(() => {
    if (!selectedShipItem || transferring) return;
    setTransferring(true);
    networkManager.sendShipToBank(selectedShipItem.itemId, transferQuantity);
  }, [selectedShipItem, transferQuantity, transferring]);

  // Count items by type for badges
  const bankCounts = useMemo(() => {
    const counts = { all: bankItems.length, equipment: 0, resource: 0 };
    bankItems.forEach(item => {
      if (item.itemType === 'equipment') counts.equipment++;
      else counts.resource++;
    });
    return counts;
  }, [bankItems]);

  const shipCounts = useMemo(() => {
    const counts = { all: shipInventory.length, equipment: 0, resource: 0 };
    shipInventory.forEach(item => {
      if (item.itemType === 'equipment') counts.equipment++;
      else counts.resource++;
    });
    return counts;
  }, [shipInventory]);

  // Render item list
  const renderItemList = (
    items: DisplayItem[],
    selectedId: string,
    onSelect: (id: string) => void,
    loading: boolean
  ) => {
    if (loading) {
      return <div style={emptyStateStyle}>Loading...</div>;
    }

    if (items.length === 0) {
      return <div style={emptyStateStyle}>No items</div>;
    }

    return (
      <div style={itemListStyle}>
        {items.map(item => (
          <div
            key={item.itemId}
            style={itemRowStyle(item.itemId === selectedId, item.color)}
            onClick={() => onSelect(item.itemId === selectedId ? '' : item.itemId)}
          >
            <div style={itemInfoStyle}>
              <span style={itemNameStyle}>{item.name}</span>
              {item.tier && (
                <span style={itemTierStyle}>T{item.tier}</span>
              )}
              {item.slotType && (
                <span style={itemTypeStyle(item.color)}>{item.slotType}</span>
              )}
            </div>
            <span style={itemQuantityStyle}>x{item.quantity}</span>
          </div>
        ))}
      </div>
    );
  };

  // Render item details panel
  const renderDetailsPanel = (
    item: DisplayItem | undefined,
    equipment: Equipment | null,
    actionLabel: string,
    onAction: () => void,
    disabled: boolean
  ) => {
    if (!item) {
      return (
        <div style={detailsPanelEmptyStyle}>
          <FiPackage size={32} style={{ opacity: 0.3 }} />
          <span>Select an item to view details</span>
        </div>
      );
    }

    return (
      <div style={detailsPanelStyle}>
        {/* Item header */}
        <div style={detailsHeaderStyle}>
          <span style={detailsNameStyle}>{item.name}</span>
          {item.tier && <span style={detailsTierStyle}>Tier {item.tier}</span>}
        </div>

        {/* Item info */}
        <div style={detailsInfoStyle}>
          <div style={detailsRowStyle}>
            <span style={detailsLabelStyle}>Type</span>
            <span style={{ color: item.color }}>{item.slotType || 'Resource'}</span>
          </div>
          <div style={detailsRowStyle}>
            <span style={detailsLabelStyle}>Quantity</span>
            <span>x{item.quantity}</span>
          </div>

          {/* Equipment-specific stats */}
          {equipment && (
            <>
              <div style={detailsRowStyle}>
                <span style={detailsLabelStyle}>Size</span>
                <span>{equipment.size}</span>
              </div>
              <div style={detailsRowStyle}>
                <span style={detailsLabelStyle}>Power</span>
                <span style={{ color: equipment.power > 0 ? PALETTE.GREEN : equipment.power < 0 ? PALETTE.RED : PALETTE.DIM }}>
                  {equipment.power > 0 ? '+' : ''}{equipment.power}
                </span>
              </div>
              <div style={detailsRowStyle}>
                <span style={detailsLabelStyle}>Volume</span>
                <span>{equipment.volume}</span>
              </div>
            </>
          )}
        </div>

        {/* Quantity selector */}
        {item.quantity > 1 && (
          <div style={quantitySelectorStyle}>
            <span style={detailsLabelStyle}>Transfer:</span>
            <div style={quantityControlsStyle}>
              <button
                style={quantityButtonStyle}
                onClick={() => setTransferQuantity(Math.max(1, transferQuantity - 1))}
                disabled={transferQuantity <= 1}
              >
                -
              </button>
              <span style={quantityValueStyle}>{Math.min(transferQuantity, item.quantity)}</span>
              <button
                style={quantityButtonStyle}
                onClick={() => setTransferQuantity(Math.min(item.quantity, transferQuantity + 1))}
                disabled={transferQuantity >= item.quantity}
              >
                +
              </button>
              <button
                style={quantityMaxButtonStyle}
                onClick={() => setTransferQuantity(item.quantity)}
              >
                Max
              </button>
            </div>
          </div>
        )}

        {/* Transfer action */}
        <button
          style={transferButtonStyle(disabled)}
          onClick={onAction}
          disabled={disabled}
        >
          {transferring ? (
            'Transferring...'
          ) : (
            <>
              {actionLabel}
              <FiArrowRight size={14} />
            </>
          )}
        </button>
      </div>
    );
  };

  // Render Bank Tab
  const renderBankTab = () => (
    <div style={tabContentStyle}>
      {/* Left pane - Bank items list */}
      <div style={leftPaneStyle}>
        <div style={paneTitleStyle}>
          <FiDatabase size={16} />
          <span>Bank Storage ({bankCounts.all})</span>
        </div>

        {/* Filters */}
        <div style={filterContainerStyle}>
          {FILTERS.map(filter => {
            const count = bankCounts[filter.id];
            return (
              <button
                key={filter.id}
                style={filterButtonStyle(bankFilter === filter.id)}
                onClick={() => setBankFilter(filter.id)}
              >
                {filter.label} {count > 0 ? `(${count})` : ''}
              </button>
            );
          })}
        </div>

        {renderItemList(displayBankItems, selectedBankItemId, setSelectedBankItemId, bankLoading)}
      </div>

      {/* Right pane - Details */}
      <div style={rightPaneStyle}>
        {renderDetailsPanel(
          selectedBankItem,
          selectedBankEquipment,
          'Move to Ship',
          handleBankToShip,
          !selectedBankItem || transferring
        )}
      </div>
    </div>
  );

  // Render Current Ship Tab
  const renderShipTab = () => (
    <div style={tabContentStyle}>
      {/* Left pane - Ship items list */}
      <div style={leftPaneStyle}>
        <div style={paneTitleStyle}>
          <FiPackage size={16} />
          <span>Ship Cargo ({shipCounts.all})</span>
        </div>

        {/* Filters */}
        <div style={filterContainerStyle}>
          {FILTERS.map(filter => {
            const count = shipCounts[filter.id];
            return (
              <button
                key={filter.id}
                style={filterButtonStyle(shipFilter === filter.id)}
                onClick={() => setShipFilter(filter.id)}
              >
                {filter.label} {count > 0 ? `(${count})` : ''}
              </button>
            );
          })}
        </div>

        {renderItemList(displayShipItems, selectedShipItemId, setSelectedShipItemId, shipLoading)}
      </div>

      {/* Right pane - Details */}
      <div style={rightPaneStyle}>
        {renderDetailsPanel(
          selectedShipItem,
          selectedShipEquipment,
          'Move to Bank',
          handleShipToBank,
          !selectedShipItem || transferring
        )}
      </div>
    </div>
  );

  return (
    <div style={containerStyle}>
      {/* Loading overlay */}
      {transferring && (
        <div style={loadingOverlayStyle}>
          <div style={loadingBoxStyle}>Transferring...</div>
        </div>
      )}

      {/* Header */}
      <div style={headerStyle}>
        <button style={backButtonStyle} onClick={onBack}>
          <FiArrowLeft size={16} />
          <span>BACK</span>
        </button>
        <div style={headerTabsStyle}>
          <button
            style={headerTabStyle(activeTab === 'bank')}
            onClick={() => setActiveTab('bank')}
          >
            Bank
          </button>
          <button
            style={headerTabStyle(activeTab === 'ship')}
            onClick={() => setActiveTab('ship')}
          >
            Current Ship
          </button>
        </div>
        <span style={hintStyle}>[ESC] Return to Station</span>
      </div>

      {/* Tab Content */}
      {activeTab === 'bank' ? renderBankTab() : renderShipTab()}

      {/* Footer */}
      <div style={footerStyle}>
        {activeTab === 'bank' ? (
          <span>Items in Bank are safe from death. Select an item to transfer to your ship.</span>
        ) : (
          <span>Items in Ship Cargo can be lost on death. Store valuable items in the Bank.</span>
        )}
      </div>
    </div>
  );
}

// ==================== STYLES ====================

const containerStyle: React.CSSProperties = { ...stationContainerStyle, position: 'relative' };
const headerStyle = stationHeaderStyle;
const backButtonStyle = stationBackButtonStyle;
const headerTabsStyle = stationHeaderTabsStyle;
const headerTabStyle = stationHeaderTabStyle;
const hintStyle = stationHintStyle;

const tabContentStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
};

const leftPaneStyle: React.CSSProperties = {
  width: '380px',
  display: 'flex',
  flexDirection: 'column',
  borderRight: `1px solid ${PALETTE.BORDER}`,
  overflow: 'hidden',
};

const rightPaneStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const paneTitleStyle: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '11px',
  fontWeight: 600,
  color: PALETTE.DIM,
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const filterContainerStyle: React.CSSProperties = {
  padding: '8px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  display: 'flex',
  gap: '6px',
};

const filterButtonStyle = (isActive: boolean): React.CSSProperties => ({
  padding: '4px 10px',
  fontSize: '11px',
  backgroundColor: isActive ? PALETTE.TEAL : 'transparent',
  color: isActive ? PALETTE.DARK : PALETTE.DIM,
  border: `1px solid ${isActive ? PALETTE.TEAL : PALETTE.DIM}`,
  borderRadius: '3px',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  fontFamily: "'JetBrains Mono', monospace",
});

const itemListStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const itemRowStyle = (isSelected: boolean, color: string): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 12px',
  backgroundColor: isSelected ? PALETTE.PANEL : 'transparent',
  border: `2px solid ${isSelected ? color : 'transparent'}`,
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
});

const itemInfoStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const itemNameStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.LIGHT,
  fontWeight: 600,
};

const itemTierStyle: React.CSSProperties = {
  fontSize: '10px',
  color: PALETTE.DIM,
  backgroundColor: PALETTE.DARK,
  padding: '2px 6px',
  borderRadius: '3px',
};

const itemTypeStyle = (color: string): React.CSSProperties => ({
  fontSize: '10px',
  color: color,
  textTransform: 'uppercase',
});

const itemQuantityStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.TEAL,
  fontWeight: 600,
};

const emptyStateStyle: React.CSSProperties = {
  padding: '40px 20px',
  textAlign: 'center',
  color: PALETTE.DIM,
  fontSize: '11px',
};

// Details panel styles
const detailsPanelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  height: '100%',
};

const detailsPanelEmptyStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  color: PALETTE.DIM,
  gap: '12px',
  fontSize: '11px',
};

const detailsHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '16px',
  paddingBottom: '12px',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
};

const detailsNameStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  color: PALETTE.LIGHT,
};

const detailsTierStyle: React.CSSProperties = {
  fontSize: '11px',
  color: PALETTE.TEAL,
  backgroundColor: `${PALETTE.TEAL}22`,
  padding: '4px 10px',
  borderRadius: '4px',
};

const detailsInfoStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1,
};

const detailsRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 0',
  borderBottom: `1px solid ${PALETTE.BORDER}`,
  fontSize: '11px',
};

const detailsLabelStyle: React.CSSProperties = {
  color: PALETTE.DIM,
};

const quantitySelectorStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 0',
  marginTop: '12px',
  borderTop: `1px solid ${PALETTE.BORDER}`,
};

const quantityControlsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const quantityButtonStyle: React.CSSProperties = {
  width: '28px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: PALETTE.PANEL,
  color: PALETTE.LIGHT,
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '11px',
  fontFamily: "'JetBrains Mono', monospace",
};

const quantityValueStyle: React.CSSProperties = {
  minWidth: '40px',
  textAlign: 'center',
  fontSize: '11px',
  fontWeight: 600,
  color: PALETTE.TEAL,
};

const quantityMaxButtonStyle: React.CSSProperties = {
  padding: '4px 10px',
  backgroundColor: PALETTE.PANEL,
  color: PALETTE.DIM,
  border: `1px solid ${PALETTE.DIM}`,
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '11px',
  fontFamily: "'JetBrains Mono', monospace",
};

const transferButtonStyle = (disabled: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '14px 20px',
  marginTop: '16px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: disabled ? PALETTE.DIM : PALETTE.TEAL,
  color: disabled ? PALETTE.LIGHT : PALETTE.DARK,
  border: 'none',
  borderRadius: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  transition: 'all 0.15s',
  fontFamily: "'JetBrains Mono', monospace",
});

const loadingOverlayStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(15, 27, 38, 0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 20,
};

const loadingBoxStyle: React.CSSProperties = {
  padding: '20px 40px',
  backgroundColor: PALETTE.PANEL,
  borderRadius: '8px',
  border: `1px solid ${PALETTE.TEAL}`,
  color: PALETTE.TEAL,
  fontSize: '11px',
};

const footerStyle = stationFooterStyle;

export default Bank;
